import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trophy, Award as AwardIcon, Crown, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function StaffRanking({ user }) {
  const [quarters, setQuarters] = useState([]);
  const [quarter, setQuarter] = useState("");
  const [ranking, setRanking] = useState(null);
  const [awards, setAwards] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/works/export", { params: { quarter }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `korban-travaux-${quarter}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch (err) {
      toast.error("Échec de l'export. Réessayez.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api
      .get("/quarters")
      .then((r) => {
        setQuarters(r.data);
        if (r.data[0]) setQuarter(r.data[0]);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const load = (q) => {
    api.get("/ranking", { params: { quarter: q } }).then((r) => setRanking(r.data)).catch(() => {});
    api.get("/awards").then((r) => setAwards(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (quarter) load(quarter);
  }, [quarter]);

  const award = async () => {
    const top = ranking?.rows?.[0];
    if (!top) return toast.error("Aucun classement pour ce trimestre.");
    setBusy(true);
    try {
      await api.put(`/awards/${quarter}`, { user_id: top.user_id });
      toast.success(`Prime du trimestre ${quarter} attribuée à ${top.name}.`);
      load(quarter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const winner = ranking?.rows?.[0];
  const winnerAwarded = awards?.some((a) => a.quarter === quarter && a.user_id === winner?.user_id);

  return (
    <div className="space-y-8">
      <section data-testid="quarterly-ranking-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-bold text-korban-navy">Classement trimestriel</h2>
          <div className="flex items-center gap-2">
            {user.role === "gerant" && (
              <button
                onClick={exportCsv}
                disabled={exporting || !quarter}
                data-testid="export-csv-button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-xs font-bold text-korban-navy ring-1 ring-slate-200 transition-all hover:-translate-y-0.5 hover:ring-korban-blue disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export CSV
              </button>
            )}
            <select
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              data-testid="ranking-select-quarter"
              className="field field-light !w-auto !py-2 text-xs font-bold"
            >
              {quarters.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Le meilleur employé du trimestre est celui qui a le plus de travaux validés « parfait » (à égalité, le plus de
          travaux au total).
        </p>

        {!ranking ? (
          <p className="mt-5 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : ranking.rows.length === 0 ? (
          <p className="mt-5 rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500" data-testid="ranking-empty">
            Aucun travail enregistré pour le trimestre {quarter}.
          </p>
        ) : (
          <div className="mt-5 space-y-3">
            {ranking.rows.map((r, i) => (
              <div
                key={r.user_id}
                data-testid={`ranking-row-${i + 1}`}
                className={`flex items-center gap-4 rounded-xl border p-4 shadow-sm ${
                  i === 0 ? "border-korban-gold bg-gradient-to-r from-[#fffaf0] to-white" : "border-slate-200 bg-white"
                }`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-lg font-black ${
                    i === 0 ? "bg-korban-gold text-korban-navy" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {i === 0 ? <Trophy size={20} className="text-korban-navy" /> : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-korban-navy">
                    {r.name}
                    {i === 0 && <Crown size={14} className="ml-1.5 inline text-korban-golddark" />}
                  </p>
                  <p className="text-xs text-slate-500">{r.dept}</p>
                  <div className="mt-2 h-1.5 w-full max-w-[220px] overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-korban-blue"
                      style={{ width: `${r.total ? Math.round((r.parfaits / r.total) * 100) : 0}%` }}
                    />
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl font-black text-emerald-600" data-testid={`ranking-parfaits-${i + 1}`}>{r.parfaits}</p>
                  <p className="text-[0.68rem] font-bold uppercase tracking-wider text-slate-400">parfaits</p>
                  <p className="mt-0.5 text-xs text-slate-500">{r.total} travaux</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {user.role === "gerant" && ranking?.rows?.length > 0 && (
          <button
            onClick={award}
            disabled={busy || winnerAwarded}
            data-testid="award-attribute-button"
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-korban-gold px-6 py-3 text-sm font-bold text-korban-navy transition-all hover:-translate-y-0.5 hover:bg-korban-golddark disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Trophy size={16} />}
            {winnerAwarded ? `Prime ${quarter} déjà attribuée` : `Attribuer la prime ${quarter} à ${winner.name}`}
          </button>
        )}
      </section>

      <section data-testid="awards-section">
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-korban-navy">
          <AwardIcon size={20} className="text-korban-golddark" /> Primes attribuées
        </h2>
        {!awards ? (
          <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : awards.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="awards-empty">
            Aucune prime attribuée pour l'instant.
          </p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {awards.map((a) => (
              <div key={a.quarter} className="flex items-center gap-3 rounded-xl border border-korban-gold/50 bg-[#fffaf0] p-4" data-testid={`award-row-${a.quarter}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-korban-gold/20">
                  <Trophy size={18} className="text-korban-golddark" />
                </span>
                <div>
                  <p className="text-[0.7rem] font-bold uppercase tracking-widest text-korban-golddark">{a.quarter}</p>
                  <p className="font-bold text-korban-navy">{a.name}</p>
                  <p className="text-xs text-slate-500">{a.score} travaux « parfait »</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
